import { useEffect, useMemo } from 'react';
import { AppDispatch } from 'store';
import { useParams } from 'react-router-dom';
import { ProjectIdParam } from 'constants/Routes';
import {
  DefaultSqlDataInterface,
  ImagesSelectValuesInterface,
  IncisionSelectItemInterface,
  IndicatorSelectItemInterface,
  ServiceSelectValuesInterface,
  SqlDependencyInterface,
  SqlDependencyTableInterface,
  TableDataSettings,
  VariablesSelectValuesInterface,
  VisualisationIdInterface,
} from 'store/reducers/visualisations/types';
import { getVisualisationAstParts, getVisualisationFieldName } from 'store/reducers/visualisations/constants';
import { setAstOfVisualisationActions } from 'store/reducers/ast/actions';
import { getVisualisationValuesAction } from 'store/reducers/visualisations/actions';
import Snackbar from 'services/Snackbar';
import { ModelIdInterface, SqlRequestInterface } from 'types/store';
import { useRLS } from 'utils/hooks/visualisation/useRLS';
import { ErrorMessageInterface } from 'types/global';

export interface SqlVisualisationTableProps
  extends VisualisationIdInterface,
    Partial<ServiceSelectValuesInterface>,
    Partial<VariablesSelectValuesInterface>,
    Partial<ImagesSelectValuesInterface>,
    Pick<SqlDependencyInterface, 'limit'>,
    SqlRequestInterface,
    ModelIdInterface {
  sqlData: DefaultSqlDataInterface;
  dispatch: AppDispatch;
  dataSettings: TableDataSettings;
}

export const useSqlVisualisationTable = ({
  sqlData: { filterAndGroupRequest },
  dispatch,
  dataSettings,
  id,
  serviceSelectValues = [],
  variablesSelectValues = [],
  imagesSelectValues = [],
  limit,
  sqlRequest,
  modelId,
}: SqlVisualisationTableProps) => {
  const projectId = useParams<ProjectIdParam>().projectId || '';
  const { rls } = useRLS();

  const sqlDataDependency = useMemo<SqlDependencyTableInterface>(() => {
    const { incisions, incisionsInHeader, isRealData, indicators, activeIncisionId } = dataSettings;

    return {
      incisions: incisions.reduce<IncisionSelectItemInterface[]>(
        (result, { id, fieldName, name, settings: { nameFromDatabase, customRequest } }) =>
          fieldName || customRequest
            ? [
                ...result,
                {
                  alias: getVisualisationFieldName({ fieldName, name, nameFromDatabase }),
                  selectSql: fieldName || '',
                  customRequest,
                  id,
                },
              ]
            : result,
        [],
      ),
      incisionsInHeader: incisionsInHeader.reduce<IncisionSelectItemInterface[]>(
        (result, { id, fieldName, name, settings: { nameFromDatabase, customRequest } }) =>
          fieldName || customRequest
            ? [
                ...result,
                {
                  alias: getVisualisationFieldName({ fieldName, name, nameFromDatabase }),
                  selectSql: fieldName || '',
                  customRequest,
                  id,
                },
              ]
            : result,
        [],
      ),
      indicators: indicators.reduce<IndicatorSelectItemInterface[]>(
        (result, { id, fieldName, name, settings: { nameFromDatabase }, customRequest, operationType }) =>
          fieldName || customRequest
            ? [
                ...result,
                {
                  alias: getVisualisationFieldName({ fieldName, name, nameFromDatabase }),
                  selectSql: fieldName || '',
                  id,
                  customRequest,
                  operationType,
                },
              ]
            : result,
        [],
      ),
      isRealData,
      activeIncisionId,
      serviceSelectValues,
      variablesSelectValues,
      imagesSelectValues,
      id,
      limit,
      filterAndGroupRequest,
    };
  }, [dataSettings, serviceSelectValues, variablesSelectValues, imagesSelectValues, id, limit, filterAndGroupRequest]);

  /* Update AST Store */
  useEffect(() => {
    const {
      id,
      indicators,
      incisionsInHeader,
      incisions,
      activeIncisionId,
      serviceSelectValues,
      variablesSelectValues,
      imagesSelectValues,
      limit,
      filterAndGroupRequest,
    } = sqlDataDependency;

    try {
      const astData = getVisualisationAstParts({
        incisions: [...incisions, ...incisionsInHeader],
        activeIncisionId,
        indicators,
        serviceSelectValues,
        variablesSelectValues,
        imagesSelectValues,
        limit,
        filterAndGroupRequest,
      });

      dispatch(setAstOfVisualisationActions({ id, astData }));
    } catch (error) {
      const message = (error as ErrorMessageInterface)?.message || '';
      Snackbar.show(message, 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sqlDataDependency)]);

  useEffect(() => {
    if (sqlRequest && dataSettings.isRealData && modelId) {
      const request = dispatch(getVisualisationValuesAction({ sqlRequest, projectId, visualisationId: id, modelId, rls }));

      return () => {
        request.abort();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqlRequest, dataSettings.isRealData, rls, modelId]);

  return { projectId };
};
